import {PickFilter, PickStationFilter} from "../pickStatus/model/PickFilter";
import {isRouteAtRisk} from "./Util";
import {
    AT_RISK, PICK_STATUS, ASSIGNMENT_AVAILABLE, ASSIGNMENT_BLOCKED,
    MANUALLY_PRIORITISED_PICKLIST, MANUALLY_ASSIGNED_PICKLIST,
    isRouteStalled, OPTIMAL_ASSIGNMENT, TIME_CRITICAL_PICKLIST,
    OVERCROWDED_AISLES, MULTI_STATION_PICKLIST_TAG_LABEL, PICK_TYPE, DWELLING_PICKLIST
} from "./EntityStatusManager";
import {AR_V2, BOOLEAN} from "./Constants";
import {
    AVAILABLE, BLOCKED, MANUAL_ASSIGNMENT, MANUALLY_PRIORITISED, OPTIMAL, OVERCROWDED,
    PICKLIST_ASSIGNABLE,
    PICKLIST_ASSIGNED,
    PICKLIST_IN_PROGRESS, PICKLIST_LOCKED, TIME_CRITICAL
} from "../pickStatus/constants/Constants";

const ROUTE_STATUS_FILTER_TAG_PREFIX = "Route: ";
const PICK_STATUS_FILTER_TAG_PREFIX = "Picklist: ";


/**
 * Filters list of routes on the basics of global filters applied in pick tab.
 *
 * @param routeList
 * @param filterState
 * @param configViewModel
 * @return {*}
 */
export function applyGlobalPickFilters(routeList, filterState, configViewModel) {
    return routeList
        .filter(route => applyFilter(route, filterState, PickFilter.CLUSTER, configViewModel))
        .filter(route => applyFilter(route, filterState, PickFilter.DRIVER_TYPE, configViewModel))
        .filter(route => applyFilter(route, filterState, PickFilter.DEPARTURE, configViewModel))
        .filter(route => applyFilter(route, filterState, PickFilter.PICK_BY_TIME, configViewModel))
        .filter(route => applyFilter(route, filterState, PickFilter.ROUTE_STATUS, configViewModel))
        .filter(route => applyFilter(route, filterState, PickFilter.HIGH_SPR, configViewModel))
        .filter(route => applyFilter(route, filterState, PickFilter.PICKLIST_STATUS, configViewModel))
        .filter(route => applyFilter(route, filterState, PickFilter.AT_RISK, configViewModel))
        .filter(route => applyFilter(route, filterState, PickFilter.MULTI_STATION_PICKLIST, configViewModel))
        .filter(route => applyLastPickRouteCutARv2Filter(route, filterState, configViewModel))
}

/**
 * Filters list of picklists and aisles in grid view on the basics of global filters applied in layout grid view tab.
 *
 * @param picklistsInAisleGroups
 * @param picklistsInWorkGroups
 * @param filterState
 * @return {*}
 */
export function applyGlobalPickStatusInGridFilters(picklistsInAisleGroups, picklistsInWorkGroups, filterState) {
    const clusters = new Set();
    let clusterToAisleGroupMapping = new Map();
    let aisleGroupToPicklistMapping = {};
    let workGroupToPicklistMapping = [];

    picklistsInAisleGroups.forEach(picklistsPerAisleGroup => {
        const aisleGroup = picklistsPerAisleGroup.aisleGroup;
        const aisleGroupAisles = picklistsPerAisleGroup.aisleGroup.aisles;
        const aisleGroupStatus = picklistsPerAisleGroup.aisleGroupStatus;

        //Apply multi station picklist filter
        let filteredPicklists = applyPicklistTypeInGridFilter(picklistsPerAisleGroup.picklists, filterState, PickFilter.MULTI_STATION_PICKLIST);
        //Apply aisle group status filter
        filteredPicklists = applyPickStatusInGridFilter(filteredPicklists, aisleGroupStatus, filterState, PickFilter.AISLE_GROUP_STATUS);
        //Apply picklist status filter
        filteredPicklists = applyPickStatusInGridFilter(filteredPicklists, aisleGroupStatus, filterState, PickFilter.PICKLIST_STATUS_IN_GRID);
        //Apply picklist status filter
        filteredPicklists = applyPickStatusInGridFilter(filteredPicklists, aisleGroupStatus, filterState, PickFilter.WAVE_PLUS_X);

        aisleGroupToPicklistMapping[aisleGroupAisles] = {
            picklists: filteredPicklists,
            aisleGroupStatus: aisleGroupStatus
        };
        if (Array.isArray(aisleGroupAisles) && aisleGroupAisles.length > 0) {
            const clusterLabel = aisleGroupAisles[0].charAt(0);
            clusters.add(clusterLabel);
            const filterValues = filterState[PickFilter.AISLE_GROUP_STATUS.contextKey];
            if (filterValues.length === 0 || filteredPicklists.length !== 0) {
                if (clusterToAisleGroupMapping.has(clusterLabel)) {
                    clusterToAisleGroupMapping.get(clusterLabel).push(aisleGroup)
                } else {
                    clusterToAisleGroupMapping.set(clusterLabel, new Array(aisleGroup))
                }
            }
        }
    });

    picklistsInWorkGroups.forEach(picklistsPerWorkGroup => {
        const workGroup = picklistsPerWorkGroup.workGroup;
        //Apply multi station picklist filter
        let filteredPicklists = applyPicklistTypeInGridFilter(picklistsPerWorkGroup.picklists, filterState, PickFilter.MULTI_STATION_PICKLIST);
        //Apply picklist status filter
        filteredPicklists = applyPickStatusInGridFilter(filteredPicklists, AVAILABLE, filterState, PickFilter.PICKLIST_STATUS_IN_GRID);
        //Apply picklist status filter
        filteredPicklists = applyPickStatusInGridFilter(filteredPicklists, AVAILABLE, filterState, PickFilter.WAVE_PLUS_X);

        workGroupToPicklistMapping.push({
            picklists: filteredPicklists,
            workGroup: workGroup
        });
    });

    return {
        clusters: Array.from(clusters).sort(),
        clusterToAisleGroupMapping: clusterToAisleGroupMapping,
        aisleGroupToPicklistMapping: aisleGroupToPicklistMapping,
        workGroupToPicklistMapping: workGroupToPicklistMapping
    };
}

/**
 * Filters list of picklists in grid view on the basics of global filters applied in layout grid view tab.
 *
 * @param picklistsInAisleGroups
 * @param picklistsInWorkGroups
 * @param filterState
 * @return list of picklists in the grid
 */
export function fetchPicklistsInGridBasedOnAppliedFilters(picklistsInAisleGroups, picklistsInWorkGroups, filterState) {
    const picklistsInAisleGroup = new Set();
    const picklistsInWorkGroup = new Set();
    const picklistToAisleGroupStatusMap = new Map();

    picklistsInAisleGroups.forEach(picklistsPerAisleGroup => {
        const aisleGroupStatus = picklistsPerAisleGroup.aisleGroupStatus;
        // Multi station picklist filter
        let filteredPicklists = applyPicklistTypeInGridFilter(picklistsPerAisleGroup.picklists, filterState, PickFilter.MULTI_STATION_PICKLIST);
        //Apply aisle group status filter
        filteredPicklists = applyPickStatusInGridFilter(filteredPicklists, aisleGroupStatus, filterState, PickFilter.AISLE_GROUP_STATUS);
        //Apply picklist status filter
        filteredPicklists = applyPickStatusInGridFilter(filteredPicklists, aisleGroupStatus, filterState, PickFilter.PICKLIST_STATUS_IN_GRID);
        //Apply picklist status filter
        filteredPicklists = applyPickStatusInGridFilter(filteredPicklists, aisleGroupStatus, filterState, PickFilter.WAVE_PLUS_X);
        filteredPicklists.forEach(picklist => {
            picklistsInAisleGroup.add(picklist);
            if (!picklistToAisleGroupStatusMap.has(picklist)) {
                picklistToAisleGroupStatusMap.set(picklist, aisleGroupStatus);
            }
        });
    });

    picklistsInWorkGroups.forEach(picklistsPerWorkGroup => {
        //Multi station picklist filter
        let filteredPicklists = applyPicklistTypeInGridFilter(picklistsPerWorkGroup.picklists, filterState, PickFilter.MULTI_STATION_PICKLIST);
        //Apply picklist status filter
        filteredPicklists = applyPickStatusInGridFilter(filteredPicklists, "aisleGroupStatus", filterState, PickFilter.PICKLIST_STATUS_IN_GRID);
        //Apply picklist status filter
        filteredPicklists = applyPickStatusInGridFilter(filteredPicklists, "aisleGroupStatus", filterState, PickFilter.WAVE_PLUS_X);
        filteredPicklists.forEach(picklist => {
            picklistsInWorkGroup.add(picklist);
        });
    });

    return {
        picklists: Array.from(picklistsInWorkGroup).concat(Array.from(picklistsInAisleGroup)),
        picklistToAisleGroupStatusMap: picklistToAisleGroupStatusMap
    };
}

const getPicklistStatusBasedOnFilter = (status, aisleStatus, assignReason) => {
    switch (status) {
        case PICKLIST_ASSIGNABLE:
            return aisleStatus === AVAILABLE ?  ASSIGNMENT_AVAILABLE : ASSIGNMENT_BLOCKED;
        case PICKLIST_ASSIGNED:
        case PICKLIST_IN_PROGRESS:
        case PICKLIST_LOCKED:
            switch (assignReason) {
                case MANUAL_ASSIGNMENT:
                    return MANUALLY_ASSIGNED_PICKLIST;
                case MANUALLY_PRIORITISED:
                    return MANUALLY_PRIORITISED_PICKLIST;
                case OPTIMAL:
                case TIME_CRITICAL:
                    return OPTIMAL_ASSIGNMENT;
                default:
                    return "Non-optimal assignment";
            }
        default:
            return "Unmapped status";
    }
};

const getAisleGroupStatusBasedOnFilter = (aisleGroupStatus) => {
    switch (aisleGroupStatus) {
        case OVERCROWDED:
           return OVERCROWDED_AISLES;
        case BLOCKED:
            return ASSIGNMENT_BLOCKED;
        default:
            return ASSIGNMENT_AVAILABLE;
    }
};

export function applyARv2PickStationFilters(rowList, filterState, configViewModel) {
    return rowList
        .filter(row => applyFilter(row, filterState, PickStationFilter.PICK_STATION, configViewModel))
        .filter(row => applyFilter(row, filterState, PickStationFilter.STATION_STATUS, configViewModel))
}

function applyPickStatusInGridFilter(picklists, aisleGroupStatus, filterState, filter) {
    const filterValues = filterState[filter.contextKey];
    if (filterValues.length === 0) {
        return picklists;
    }
    if (filter === PickFilter.AISLE_GROUP_STATUS) {
        return applyPicklistStatusInGridEqualityFilter(getAisleGroupStatusBasedOnFilter(aisleGroupStatus), filterValues, false) ? picklists : []

    } else if (filter === PickFilter.PICKLIST_STATUS_IN_GRID) {
         return picklists
             .filter(picklist =>
                 applyPicklistStatusInGridEqualityFilter(getPicklistStatusBasedOnFilter(picklist.status, aisleGroupStatus,
                 picklist.assignmentReason), filterValues, picklist.timeCritical, picklist.isDwelling));
    } else if (filter === PickFilter.WAVE_PLUS_X) {
        return picklists.filter(picklist => picklist.wavePlusXPicklist);
    }
}

function applyPicklistTypeInGridFilter(picklists, filterState, filter) {
    if (!picklists || !picklists.length) {
        return [];
    }

    if (filterState && filterState.multiStationPicklists &&
        filterState.multiStationPicklists.includes(BOOLEAN.TRUE) && filter === PickFilter.MULTI_STATION_PICKLIST) {
        return picklists.filter(picklist => picklist.picklistType === PICK_TYPE.MULTI_STATION_PICKLIST);
    }
    return picklists;
}

function applyFilter(route, filterState, filter, configViewModel) {
    const filterValues = filterState[filter.contextKey];

    if (!filterValues || filterValues.length === 0) {
        return true;
    }

    if (filter === PickFilter.HIGH_SPR) {
        return applyHighSPRFilter(route, filterState)
    } else if (filter === PickFilter.PICKLIST_STATUS) {
        return applyPickStatusFilter(route, filterValues, filter)
    } else if (filter === PickFilter.ROUTE_STATUS) {
        return applyEqualityFilter(route, filterValues, filter)
    } else if (filter === PickFilter.AT_RISK) {
        return applyAtRiskFilter(route, filterState, configViewModel)
    } else if (filter === PickFilter.MULTI_STATION_PICKLIST) {
        return applyMultiStationPicklistFilter(route, filterState, configViewModel)
    } else {
        return applyContainsFilter(route, filterValues, filter)
    }
}

function applyEqualityFilter(route, filterValues, filter) {
    let result = false;
    filterValues.forEach(filterValue => {
        if (route[filter.modelKey] === filterValue) {
            result = true;
        }
    })
    return result;
}

function applyPicklistStatusInGridEqualityFilter(status, filterValues, timeCritical, isDwelling) {
    let result = false;
    filterValues.forEach(filterValue => {
        if (status === filterValue) {
            result = true;
        } else if (timeCritical && TIME_CRITICAL_PICKLIST === filterValue) {
            result = true;
        } else if (isDwelling && DWELLING_PICKLIST === filterValue) {
            result = true;
        }
    });
    return result;
}

function applyContainsFilter(route, filterValues, filter) {
    let result = false;
    filterValues.forEach(filterValue => {
        if (route[filter.modelKey] && route[filter.modelKey].includes(filterValue)) {
            result = true;
        }
    })
    return result;
}

function applyHighSPRFilter(route, filterState) {
    return route.routeSPR >= filterState.sprPackageCount;
}

function applyAtRiskFilter(route, filterState, configViewModel) {
    const atRisk = isRouteAtRisk(route, filterState.sprPackageCount, filterState.sprTimeLimit);
    const isStalled = configViewModel.stationConfig && configViewModel.stationConfig.atRiskMetricV2 && isRouteStalled(route);
    return atRisk || isStalled;
}

const applyMultiStationPicklistFilter = (route) => {
    return route.picklists.some((picklist) => picklist.picklistType === PICK_TYPE.MULTI_STATION_PICKLIST);
};

function applyPickStatusFilter(route, filterValues, filter) {
    let result = false;
    route.picklists.forEach(picklist => {
        filterValues.forEach(filterValue => {
            if (picklist[filter.modelKey] === filterValue) {
                result = true;
            }
        })
    })

    return result;
}

function applyLastPickRouteCutARv2Filter(route, filterState, configViewModel) {
    /**
     * show route if it can be cut according to thresholds set in the filter
     * condition for filtering: there hasn’t been a pick in the last X mins (>= X) and the packages pending to be picked are <= Y
     * filtered routes can be cut manually using Cut route option in last column of table
     */
    if (typeof configViewModel === "undefined" || configViewModel.capability !== AR_V2) {
        return true;
    }

    const packagesPendingThreshold = parseInt(filterState.rcPackageCount, 10)
    const timeLimitInMinutes = parseInt(filterState.rcTimeLimit, 10)

    if (isNaN(timeLimitInMinutes) || isNaN(packagesPendingThreshold) || packagesPendingThreshold === 0) {
        return true;
    }

    const packagesPendingToBePicked = route.rswsPickProgress.totalCount - route.rswsPickProgress.completedCount;
    const minutesSinceLastPicked = route.rswsPickProgress.lastCompletionTime !== undefined ? (Date.now() - route.rswsPickProgress.lastCompletionTime) / 60000 : -1;

    return (
        route.status === PICK_STATUS.IN_PROGRESS &&
        minutesSinceLastPicked >= timeLimitInMinutes
        && packagesPendingToBePicked <= packagesPendingThreshold
    );
}


/**
 * Return display label for filter tags
 * @param key
 * @param value
 * @return {string|*}
 */
export function getFilterTagLabel(key, value) {
    if (key === PickFilter.ROUTE_STATUS.contextKey) {
        return ROUTE_STATUS_FILTER_TAG_PREFIX + value;
    } else if(key === PickFilter.PICKLIST_STATUS.contextKey){
        return PICK_STATUS_FILTER_TAG_PREFIX + value;
    } else if(key === PickFilter.AT_RISK.contextKey){
        return AT_RISK;
    } else if(key === PickFilter.MULTI_STATION_PICKLIST.contextKey){
        return MULTI_STATION_PICKLIST_TAG_LABEL;
    }
    return value;
}


// WEBPACK FOOTER //
// ./src/components/util/FilterUtil.js